import React from 'react';
import { FiX } from 'react-icons/fi';
import Icon from '../common/Icon';
import AwardeeSearchSelect from './AwardeeSearchSelect';
import { TenderAwardeeSearchHit } from '../../services/tenderAwardeeService';
import { CreateTenderData, TenderAssignmentInput } from '../../services/tenderService';
import { Tender } from '../../types';
import { useAppData } from '../../context/AppDataContext';
import {
  defaultTenderAssignmentRow,
  TENDER_ASSIGNMENT_STATUS_OPTIONS,
} from '../../utils/tenderFormUtils';

export interface TenderFormModalProps {
  isOpen: boolean;
  editingTender: Tender | null;
  formData: CreateTenderData;
  setFormData: React.Dispatch<React.SetStateAction<CreateTenderData>>;
  selectedAwardeeName: string;
  setSelectedAwardeeName: (name: string) => void;
  error?: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

const TenderFormModal: React.FC<TenderFormModalProps> = ({
  isOpen,
  editingTender,
  formData,
  setFormData,
  selectedAwardeeName,
  setSelectedAwardeeName,
  error,
  onClose,
  onSubmit,
}) => {
  const { users, brands } = useAppData();

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-3">
      <div className="relative top-2 sm:top-6 mx-auto p-3 border w-full max-w-5xl shadow-lg rounded-md bg-white max-h-[96vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">
            {editingTender ? 'Edit tender' : 'Add tender'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-0.5 -mr-0.5"
            aria-label="Close"
          >
            <Icon component={FiX} className="w-4 h-4" />
          </button>
        </div>
        {error && (
          <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
            {error}
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-1.5">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-x-2 gap-y-1.5">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Date *</label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Awarder *</label>
              <input
                type="text"
                required
                value={formData.awarder || ''}
                onChange={(e) => setFormData({ ...formData, awarder: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <AwardeeSearchSelect
                required
                value={formData.awardee_id ?? null}
                displayName={selectedAwardeeName}
                onChange={(hit: TenderAwardeeSearchHit | null) => {
                  if (!hit) {
                    setFormData((prev) => ({ ...prev, awardee_id: undefined }));
                    setSelectedAwardeeName('');
                    return;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    awardee_id: hit.id,
                    awardee_city: hit.awardee_city || '',
                    awardee_phone: hit.awardee_phone || '',
                    awardee_email: hit.awardee_email || '',
                  }));
                  setSelectedAwardeeName(hit.name);
                }}
              />
            </div>
            <div className="col-span-2 sm:col-span-4 xl:col-span-6">
              <p className="text-[10px] text-gray-500">
                Changes to city, phone, or email apply to this tender only, not the master awardee record.
              </p>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Awardee city</label>
              <input
                type="text"
                value={formData.awardee_city || ''}
                onChange={(e) => setFormData({ ...formData, awardee_city: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Awardee phone</label>
              <input
                type="tel"
                value={formData.awardee_phone || ''}
                onChange={(e) => setFormData({ ...formData, awardee_phone: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Awardee email</label>
              <input
                type="email"
                value={formData.awardee_email || ''}
                onChange={(e) => setFormData({ ...formData, awardee_email: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Work order value</label>
              <input
                type="number"
                step="0.01"
                value={formData.work_order_value ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    work_order_value: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Industry</label>
              <input
                type="text"
                value={formData.industry || ''}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">City</label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Completion months</label>
              <input
                type="number"
                value={formData.completion_months ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    completion_months: e.target.value ? parseInt(e.target.value, 10) : undefined,
                  })
                }
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Product req</label>
              <input
                type="text"
                value={formData.product_req || ''}
                onChange={(e) => setFormData({ ...formData, product_req: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Qty req</label>
              <input
                type="text"
                value={formData.qty_req || ''}
                onChange={(e) => setFormData({ ...formData, qty_req: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Price</label>
              <input
                type="number"
                step="0.01"
                value={formData.price ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div className="col-span-2 sm:col-span-2 md:col-span-2 xl:col-span-2">
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Source</label>
              <input
                type="text"
                value={formData.source || ''}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                placeholder="e.g. Portal, Reference"
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div className="col-span-2 sm:col-span-2 md:col-span-2 xl:col-span-2">
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Brands in use</label>
              <input
                type="text"
                value={formData.brands_in_use || ''}
                onChange={(e) => setFormData({ ...formData, brands_in_use: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
              />
            </div>
            <div className="col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-6">
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Scope of work</label>
              <textarea
                value={formData.scope_of_work || ''}
                onChange={(e) => setFormData({ ...formData, scope_of_work: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug resize-y min-h-[2rem]"
                rows={2}
              />
            </div>
            <div className="col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-6">
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Update status</label>
              <textarea
                value={formData.update_status || ''}
                onChange={(e) => setFormData({ ...formData, update_status: e.target.value })}
                className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug resize-y min-h-[2rem]"
                rows={2}
              />
            </div>
            <div className="col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-6">
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                Assignments (brand, user, status) *
              </label>
              <div className="space-y-1">
                {(formData.assignments || []).map((a, idx) => (
                  <div key={idx} className="flex flex-wrap gap-1.5 items-center">
                    <select
                      value={a.brand_id ?? ''}
                      onChange={(e) => {
                        const next = [...(formData.assignments || [])];
                        next[idx] = {
                          ...next[idx],
                          brand_id: e.target.value ? parseInt(e.target.value, 10) : undefined,
                        };
                        setFormData({ ...formData, assignments: next });
                      }}
                      className="border border-gray-300 rounded px-1.5 py-0.5 text-xs min-w-[7rem] flex-1 sm:flex-none sm:min-w-[8rem]"
                    >
                      <option value="">Brand</option>
                      {brands.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={a.user_id || ''}
                      onChange={(e) => {
                        const next = [...(formData.assignments || [])];
                        next[idx] = { ...next[idx], user_id: parseInt(e.target.value, 10) || 0 };
                        setFormData({ ...formData, assignments: next });
                      }}
                      className="border border-gray-300 rounded px-1.5 py-0.5 text-xs min-w-[7rem] flex-1 sm:flex-none sm:min-w-[9rem]"
                    >
                      <option value="">User</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={
                        a.status &&
                        TENDER_ASSIGNMENT_STATUS_OPTIONS.includes(
                          a.status as NonNullable<TenderAssignmentInput['status']>
                        )
                          ? a.status
                          : 'PROSPECT'
                      }
                      onChange={(e) => {
                        const next = [...(formData.assignments || [])];
                        next[idx] = {
                          ...next[idx],
                          status: e.target.value as TenderAssignmentInput['status'],
                        };
                        setFormData({ ...formData, assignments: next });
                      }}
                      className="border border-gray-300 rounded px-1.5 py-0.5 text-xs min-w-[6.5rem] flex-1 sm:flex-none sm:min-w-[7.5rem]"
                      aria-label="Assignment status"
                    >
                      {TENDER_ASSIGNMENT_STATUS_OPTIONS.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        const next = (formData.assignments || []).filter((_, i) => i !== idx);
                        setFormData({
                          ...formData,
                          assignments: next.length ? next : [defaultTenderAssignmentRow()],
                        });
                      }}
                      className="text-red-600 hover:text-red-800 p-0.5"
                      aria-label="Remove assignment"
                    >
                      <Icon component={FiX} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      assignments: [...(formData.assignments || []), defaultTenderAssignmentRow()],
                    })
                  }
                  className="text-yellow-700 hover:text-yellow-800 text-xs font-medium"
                >
                  + Add row
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-1.5 pt-1.5 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-2.5 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-xs font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-2.5 py-1 bg-yellow-500 text-black-950 rounded hover:bg-yellow-600 text-xs font-bold"
            >
              {editingTender ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenderFormModal;
